import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Edit2, Check, AlertTriangle, Plus, Trash2, UserPlus, Search, RefreshCw, Database, CheckCircle, XCircle, Clock } from 'lucide-react'
import { getVehicleById, updateVehicle, createVehicle, addOwner, removeOwner, updateOwnership, forceEnrichment, getVesCache, getRapidCarCheckCache, getMotHistory } from '../services/vehicleService.js'
import { getUsers } from '../services/userService.js'
import { useToast } from '../components/Toast.jsx'
import Modal from '../components/Modal.jsx'
import FormField from '../components/FormField.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import Breadcrumbs from '../components/Breadcrumbs.jsx'
import { statusColors, STATUS_OPTIONS, FUEL_OPTIONS, OWNERSHIP_OPTIONS, ownershipColors } from '../constants/statusColors.js'
import { validateVehicleForm, hasErrors } from '../utils/validation.js'
import { formatDate } from '../utils/dates.js'

const inputCls = 'w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'

const Section = ({ title, children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">{title}</h3>
    {children}
  </div>
)

const Field = ({ label, value, mono = false, className = '' }) => (
  <div>
    <p className="text-xs text-gray-400 mb-0.5">{label}</p>
    <p className={`text-sm font-medium ${mono ? 'font-mono' : ''} ${className}`}>{value || '-'}</p>
  </div>
)

const DateField = ({ label, value }) => {
  const isExpired = value && new Date(value) < new Date()
  return <Field label={label} value={formatDate(value)} className={isExpired ? 'text-red-400' : ''} />
}

const VehicleDetailPage = () => {
  const { vehicleUuid } = useParams()
  const toast = useToast()

  const [vehicle, setVehicle] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Enrichment
  const [enriching, setEnriching] = useState(false)
  const [vesCache, setVesCache] = useState(null)
  const [vesCacheLoading, setVesCacheLoading] = useState(false)
  const [rccCache, setRccCache] = useState(null)
  const [rccCacheLoading, setRccCacheLoading] = useState(false)
  const [motHistory, setMotHistory] = useState(null)
  const [motHistoryLoading, setMotHistoryLoading] = useState(false)

  // Edit vehicle modal
  const [editOpen, setEditOpen] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [formDirty, setFormDirty] = useState(false)
  const [confirmClose, setConfirmClose] = useState(false)

  // Add owner modal
  const [addOwnerOpen, setAddOwnerOpen] = useState(false)
  const [ownerSearch, setOwnerSearch] = useState('')
  const [ownerSearchResults, setOwnerSearchResults] = useState([])
  const [ownerSearchLoading, setOwnerSearchLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [ownerType, setOwnerType] = useState('OWNER')
  const [addingOwner, setAddingOwner] = useState(false)

  // Remove owner confirm
  const [removeOwnerTarget, setRemoveOwnerTarget] = useState(null)
  const [removingOwner, setRemovingOwner] = useState(false)

  // Edit ownership modal
  const [editOwnerTarget, setEditOwnerTarget] = useState(null)
  const [editOwnerType, setEditOwnerType] = useState('')
  const [savingOwner, setSavingOwner] = useState(false)

  const fetchVehicle = useCallback(() => {
    setLoading(true)
    setError(null)
    getVehicleById(vehicleUuid)
      .then((res) => setVehicle(res.data))
      .catch(() => setError('Failed to load vehicle details'))
      .finally(() => setLoading(false))
  }, [vehicleUuid])

  useEffect(() => { fetchVehicle() }, [fetchVehicle])

  // --- Force enrichment ---
  const handleEnrich = async () => {
    setEnriching(true)
    try {
      await forceEnrichment(vehicleUuid)
      toast.success('Enrichment triggered! Data will update shortly.')
      // Refresh after a short delay to show updated enrichment status
      setTimeout(fetchVehicle, 2000)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to trigger enrichment')
    } finally {
      setEnriching(false)
    }
  }

  // --- Edit vehicle ---
  const openEdit = useCallback(() => {
    if (!vehicle) return
    setEditForm({
      plate: vehicle.plate || '',
      status: vehicle.status || 'ACTIVE',
      mileage: vehicle.mileage ?? '',
      notes: vehicle.notes || '',
      insuranceExpiryDate: vehicle.insuranceExpiryDate || '',
      technicalInspectionExpiryDate: vehicle.technicalInspectionExpiryDate || '',
      taxDueDate: vehicle.taxDueDate || '',
    })
    setFormErrors({})
    setSaveError(null)
    setFormDirty(false)
    setEditOpen(true)
  }, [vehicle])

  const handleCloseEdit = useCallback(() => {
    if (formDirty) setConfirmClose(true)
    else setEditOpen(false)
  }, [formDirty])

  const updateField = useCallback((field, value) => {
    setEditForm(f => ({ ...f, [field]: value }))
    setFormDirty(true)
    setFormErrors(prev => ({ ...prev, [field]: undefined }))
  }, [])

  const saveEdit = async () => {
    const errors = validateVehicleForm(editForm)
    if (hasErrors(errors)) { setFormErrors(errors); return }
    setSaving(true)
    setSaveError(null)
    try {
      const data = { ...editForm }
      if (data.mileage === '') delete data.mileage
      else data.mileage = Number(data.mileage)
      if (!data.insuranceExpiryDate) delete data.insuranceExpiryDate
      if (!data.technicalInspectionExpiryDate) delete data.technicalInspectionExpiryDate
      if (!data.taxDueDate) delete data.taxDueDate
      await updateVehicle(vehicleUuid, data)
      setEditOpen(false)
      toast.success('Vehicle updated successfully')
      fetchVehicle()
    } catch {
      setSaveError('Failed to update vehicle.')
    } finally {
      setSaving(false)
    }
  }

  // --- Add owner ---
  useEffect(() => {
    if (!addOwnerOpen || !ownerSearch.trim()) {
      setOwnerSearchResults([])
      return
    }
    const timer = setTimeout(() => {
      setOwnerSearchLoading(true)
      getUsers(0, 10, ownerSearch)
        .then((res) => setOwnerSearchResults(res.data.content || []))
        .catch(() => setOwnerSearchResults([]))
        .finally(() => setOwnerSearchLoading(false))
    }, 400)
    return () => clearTimeout(timer)
  }, [ownerSearch, addOwnerOpen])

  const handleAddOwner = async () => {
    if (!selectedUser) return
    setAddingOwner(true)
    try {
      await addOwner(vehicleUuid, {
        userUuid: selectedUser.id,
        ownershipType: ownerType,
      })
      setAddOwnerOpen(false)
      setSelectedUser(null)
      setOwnerSearch('')
      setOwnerType('OWNER')
      toast.success('Owner added successfully')
      fetchVehicle()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add owner')
    } finally {
      setAddingOwner(false)
    }
  }

  // --- Remove owner ---
  const handleRemoveOwner = async () => {
    if (!removeOwnerTarget) return
    setRemovingOwner(true)
    try {
      await removeOwner(vehicleUuid, removeOwnerTarget.userUuid)
      setRemoveOwnerTarget(null)
      toast.success('Owner removed successfully')
      fetchVehicle()
    } catch {
      toast.error('Failed to remove owner')
    } finally {
      setRemovingOwner(false)
    }
  }

  // --- Edit ownership ---
  const openEditOwner = useCallback((owner) => {
    setEditOwnerTarget(owner)
    setEditOwnerType(owner.ownershipType)
  }, [])

  const handleUpdateOwnership = async () => {
    if (!editOwnerTarget) return
    setSavingOwner(true)
    try {
      await updateOwnership(vehicleUuid, editOwnerTarget.userUuid, {
        ownershipType: editOwnerType,
      })
      setEditOwnerTarget(null)
      toast.success('Ownership updated')
      fetchVehicle()
    } catch {
      toast.error('Failed to update ownership')
    } finally {
      setSavingOwner(false)
    }
  }

  const breadcrumbs = [
    { label: 'Vehicles', to: '/vehicles' },
    { label: vehicle?.plate || 'Vehicle Details' },
  ]

  if (loading) {
    return (
      <div className="space-y-4">
        <Breadcrumbs items={[{ label: 'Vehicles', to: '/vehicles' }, { label: 'Loading...' }]} />
        <div className="text-center py-20 text-gray-400">Loading vehicle details...</div>
      </div>
    )
  }

  if (error && !vehicle) {
    return (
      <div className="space-y-4">
        <Breadcrumbs items={[{ label: 'Vehicles', to: '/vehicles' }, { label: 'Error' }]} />
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
          <button onClick={fetchVehicle} className="ml-auto underline hover:no-underline">Retry</button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold font-mono">{vehicle.plate}</h1>
            <span className="text-sm text-gray-400">{vehicle.plateCountry}</span>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusColors[vehicle.status] || 'bg-gray-500/20 text-gray-400'}`}>
              {vehicle.status}
            </span>
          </div>
          <p className="text-sm text-gray-400">
            {[vehicle.brandName, vehicle.modelName].filter(Boolean).join(' ')}
            {vehicle.productionYear ? ` (${vehicle.productionYear})` : ''}
            {vehicle.vin ? ` \u00B7 VIN: ${vehicle.vin}` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleEnrich} disabled={enriching}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 border border-amber-500/30 text-amber-400 font-semibold rounded-xl hover:bg-amber-500/30 transition-all text-sm disabled:opacity-50"
            title="Force re-fetch vehicle data from external APIs">
            <RefreshCw className={`w-4 h-4 ${enriching ? 'animate-spin' : ''}`} />
            {enriching ? 'Enriching...' : 'Re-enrich'}
          </button>
          <button onClick={openEdit}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm">
            <Edit2 className="w-4 h-4" /> Edit
          </button>
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Section title="Basic Info">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Plate" value={vehicle.plate} mono />
            <Field label="Country" value={vehicle.plateCountry} />
            <Field label="VIN" value={vehicle.vin} mono />
            <Field label="Year" value={vehicle.productionYear} />
            <Field label="Color" value={vehicle.color} />
            <Field label="Fuel" value={vehicle.fuelType} />
            <Field label="Transmission" value={vehicle.transmissionType} />
            <Field label="Category" value={vehicle.category} />
          </div>
        </Section>

        <Section title="Technical">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Engine" value={vehicle.engine} />
            <Field label="Modification" value={vehicle.modification} />
            <Field label="Doors" value={vehicle.doorsCount} />
            <Field label="Seats" value={vehicle.seatsCount} />
            <Field label="Power" value={vehicle.enginePowerHp ? `${vehicle.enginePowerHp} hp` : null} />
            <Field label="Displacement" value={vehicle.engineDisplacementCc ? `${vehicle.engineDisplacementCc} cc` : null} />
            <Field label="CO2" value={vehicle.co2Emissions ? `${vehicle.co2Emissions} g/km` : null} />
            <Field label="Mileage" value={vehicle.mileage != null ? `${vehicle.mileage.toLocaleString()} km` : null} />
          </div>
        </Section>

        <Section title="Dates">
          <div className="grid grid-cols-1 gap-4">
            <DateField label="First Registration" value={vehicle.firstRegistrationDate} />
            <DateField label="Insurance Expiry" value={vehicle.insuranceExpiryDate} />
            <DateField label="MOT Expiry" value={vehicle.technicalInspectionExpiryDate} />
            <DateField label="Tax Due" value={vehicle.taxDueDate} />
          </div>
        </Section>

        {(vehicle.purchasePrice || vehicle.purchaseDate) && (
          <Section title="Purchase">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price" value={vehicle.purchasePrice ? `${vehicle.purchasePrice.toLocaleString()} BGN` : null} />
              <DateField label="Date" value={vehicle.purchaseDate} />
            </div>
          </Section>
        )}

        <Section title="Enrichment" className="lg:col-span-2">
          {vehicle.enrichment ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Status</p>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                    vehicle.enrichment.status === 'COMPLETE' ? 'bg-green-500/20 text-green-400' :
                    vehicle.enrichment.status === 'PARTIAL' ? 'bg-amber-500/20 text-amber-400' :
                    vehicle.enrichment.status === 'FAILED' ? 'bg-red-500/20 text-red-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{vehicle.enrichment.status || 'NONE'}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">DVLA/VES</p>
                  {vehicle.enrichment.dvlaEnriched
                    ? <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-4 h-4" /> Enriched</span>
                    : <span className="flex items-center gap-1 text-gray-400 text-sm"><XCircle className="w-4 h-4" /> Not enriched</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">MOT History</p>
                  {vehicle.enrichment.motEnriched
                    ? <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-4 h-4" /> Enriched</span>
                    : <span className="flex items-center gap-1 text-gray-400 text-sm"><XCircle className="w-4 h-4" /> Not enriched</span>}
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">RapidCarCheck</p>
                  {vehicle.enrichment.rapidCarEnriched
                    ? <span className="flex items-center gap-1 text-green-400 text-sm"><CheckCircle className="w-4 h-4" /> Enriched</span>
                    : <span className="flex items-center gap-1 text-gray-400 text-sm"><XCircle className="w-4 h-4" /> Not enriched</span>}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="MOT Status" value={vehicle.enrichment.motStatus} />
                <Field label="Tax Status" value={vehicle.enrichment.taxStatus} />
                <Field label="Euro Status" value={vehicle.enrichment.euroStatus} />
                <Field label="Body Type" value={vehicle.enrichment.bodyType} />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <DateField label="V5C Issued" value={vehicle.enrichment.v5cIssuedDate} />
                <Field label="Last Enrichment" value={vehicle.enrichment.lastEnrichmentAt ? new Date(vehicle.enrichment.lastEnrichmentAt).toLocaleString() : null} />
                {vehicle.enrichment.lastEnrichmentError && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Last Error</p>
                    <p className="text-sm text-red-400">{vehicle.enrichment.lastEnrichmentError}</p>
                  </div>
                )}
              </div>
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-2">
                <button onClick={async () => {
                  setVesCacheLoading(true)
                  try {
                    const res = await getVesCache(vehicle.plate)
                    setVesCache(res.data)
                  } catch { setVesCache(null); toast.error('No VES cache data found for this plate') }
                  finally { setVesCacheLoading(false) }
                }} disabled={vesCacheLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 text-blue-400 text-sm rounded-lg hover:bg-blue-500/20 transition disabled:opacity-50">
                  <Database className="w-4 h-4" />
                  {vesCacheLoading ? 'Loading...' : 'View Raw VES Data'}
                </button>
                <button onClick={async () => {
                  setRccCacheLoading(true)
                  try {
                    const res = await getRapidCarCheckCache(vehicle.plate)
                    setRccCache(res.data)
                  } catch { setRccCache(null); toast.error('No RapidCarCheck data found for this plate') }
                  finally { setRccCacheLoading(false) }
                }} disabled={rccCacheLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 text-purple-400 text-sm rounded-lg hover:bg-purple-500/20 transition disabled:opacity-50">
                  <Database className="w-4 h-4" />
                  {rccCacheLoading ? 'Loading...' : 'View RapidCarCheck Data'}
                </button>
                <button onClick={async () => {
                  setMotHistoryLoading(true)
                  try {
                    const res = await getMotHistory(vehicle.plate)
                    setMotHistory(res.data)
                  } catch { setMotHistory(null); toast.error('No MOT history found for this plate') }
                  finally { setMotHistoryLoading(false) }
                }} disabled={motHistoryLoading}
                  className="flex items-center gap-2 px-3 py-1.5 bg-green-500/10 border border-green-500/30 text-green-400 text-sm rounded-lg hover:bg-green-500/20 transition disabled:opacity-50">
                  <Database className="w-4 h-4" />
                  {motHistoryLoading ? 'Loading...' : 'View MOT History'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400">No enrichment data</p>
          )}
        </Section>

        {/* VES Cache Raw Data Modal */}
        {vesCache && (
          <Section title="VES Cache Data" className="lg:col-span-3">
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Plate" value={vesCache.plate} mono />
                <Field label="Tax Status" value={vesCache.taxStatus} />
                <Field label="MOT Status" value={vesCache.motStatus} />
                <DateField label="MOT Expiry" value={vesCache.motExpiryDate} />
                <DateField label="Tax Due" value={vesCache.taxDueDate} />
                <Field label="API Success" value={vesCache.apiSuccess ? 'Yes' : 'No'} />
                <Field label="API Calls" value={vesCache.apiCallsCount} />
                <Field label="Last Call" value={vesCache.lastApiCallAt ? new Date(vesCache.lastApiCallAt).toLocaleString() : null} />
              </div>
              {vesCache.errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  {vesCache.errorMessage}
                </div>
              )}
              {vesCache.enrichedData && (
                <div>
                  <p className="text-xs text-gray-400 mb-2 font-semibold uppercase">Enriched Data</p>
                  <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl text-xs overflow-x-auto max-h-96 border border-gray-200 dark:border-gray-700">
                    {(() => { try { return JSON.stringify(JSON.parse(vesCache.enrichedData), null, 2) } catch { return vesCache.enrichedData } })()}
                  </pre>
                </div>
              )}
              {vesCache.rawResponse && (
                <details>
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300 font-semibold uppercase">Raw API Response (click to expand)</summary>
                  <pre className="mt-2 bg-gray-50 dark:bg-gray-900 p-4 rounded-xl text-xs overflow-x-auto max-h-96 border border-gray-200 dark:border-gray-700">
                    {(() => { try { return JSON.stringify(JSON.parse(vesCache.rawResponse), null, 2) } catch { return vesCache.rawResponse } })()}
                  </pre>
                </details>
              )}
              <button onClick={() => setVesCache(null)} className="text-xs text-gray-400 hover:text-white">Close VES Data</button>
            </div>
          </Section>
        )}

        {/* RapidCarCheck Cache Raw Data */}
        {rccCache && (
          <Section title="RapidCarCheck Cache Data" className="lg:col-span-3">
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Plate" value={rccCache.plate} mono />
                <Field label="VIN" value={rccCache.vin} mono />
                <Field label="API Success" value={rccCache.apiSuccess ? 'Yes' : 'No'} />
                <Field label="API Calls" value={rccCache.apiCallsCount} />
                <Field label="Created" value={rccCache.createdAt ? new Date(rccCache.createdAt).toLocaleString() : null} />
                <Field label="Last Call" value={rccCache.lastApiCallAt ? new Date(rccCache.lastApiCallAt).toLocaleString() : null} />
                <Field label="Expires" value={rccCache.expiresAt ? new Date(rccCache.expiresAt).toLocaleString() : null} />
              </div>
              {rccCache.errorMessage && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
                  {rccCache.errorMessage}
                </div>
              )}
              {rccCache.enrichedData && (
                <div>
                  <p className="text-xs text-gray-400 mb-2 font-semibold uppercase">Enriched Data</p>
                  <pre className="bg-gray-50 dark:bg-gray-900 p-4 rounded-xl text-xs overflow-x-auto max-h-96 border border-gray-200 dark:border-gray-700">
                    {(() => { try { return JSON.stringify(JSON.parse(rccCache.enrichedData), null, 2) } catch { return rccCache.enrichedData } })()}
                  </pre>
                </div>
              )}
              {rccCache.rawResponse && (
                <details>
                  <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300 font-semibold uppercase">Raw API Response (click to expand)</summary>
                  <pre className="mt-2 bg-gray-50 dark:bg-gray-900 p-4 rounded-xl text-xs overflow-x-auto max-h-96 border border-gray-200 dark:border-gray-700">
                    {(() => { try { return JSON.stringify(JSON.parse(rccCache.rawResponse), null, 2) } catch { return rccCache.rawResponse } })()}
                  </pre>
                </details>
              )}
              <button onClick={() => setRccCache(null)} className="text-xs text-gray-400 hover:text-white">Close RapidCarCheck Data</button>
            </div>
          </Section>
        )}

        {/* MOT History Data */}
        {motHistory && (
          <Section title="MOT History (DVSA)" className="lg:col-span-3">
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Field label="Registration" value={motHistory.registration} mono />
                <Field label="Make" value={motHistory.make} />
                <Field label="Model" value={motHistory.model} />
                <Field label="Fuel" value={motHistory.fuelType} />
                <Field label="Colour" value={motHistory.primaryColour} />
                <Field label="First Used" value={motHistory.firstUsedDate} />
                <Field label="Registration Date" value={motHistory.registrationDate} />
                <Field label="Total MOT Tests" value={motHistory.motTests?.length || 0} />
              </div>

              {motHistory.motTests && motHistory.motTests.length > 0 && (
                <div>
                  <p className="text-xs text-gray-400 mb-2 font-semibold uppercase">MOT Test History</p>
                  <div className="space-y-2">
                    {motHistory.motTests.map((test, idx) => (
                      <div key={test.motTestNumber || idx}
                        className={`p-3 rounded-xl border ${test.testResult === 'PASSED' ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${test.testResult === 'PASSED' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                              {test.testResult}
                            </span>
                            <span className="text-sm font-medium">{test.completedDate}</span>
                            {test.expiryDate && <span className="text-xs text-gray-400">Expires: {test.expiryDate}</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-400">
                            {test.odometerValue && <span>{parseInt(test.odometerValue).toLocaleString()} {test.odometerUnit}</span>}
                            {test.motTestNumber && <span className="font-mono">{test.motTestNumber}</span>}
                          </div>
                        </div>
                        {test.defects && test.defects.length > 0 && (
                          <div className="mt-2 space-y-1">
                            {test.defects.map((d, di) => (
                              <div key={di} className={`text-xs px-2 py-1 rounded ${d.dangerous ? 'bg-red-500/10 text-red-400' : d.type === 'MAJOR' ? 'bg-amber-500/10 text-amber-400' : 'bg-gray-500/10 text-gray-400'}`}>
                                <span className="font-semibold">[{d.type}{d.dangerous ? ' DANGEROUS' : ''}]</span> {d.text}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setMotHistory(null)} className="text-xs text-gray-400 hover:text-white">Close MOT History</button>
            </div>
          </Section>
        )}

        {vehicle.notes && (
          <Section title="Notes">
            <p className="text-sm whitespace-pre-wrap">{vehicle.notes}</p>
          </Section>
        )}
      </div>

      {/* Owners */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Owners ({vehicle.owners?.length || 0})</h2>
          <button onClick={() => { setAddOwnerOpen(true); setSelectedUser(null); setOwnerSearch(''); setOwnerType('OWNER') }}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all text-sm">
            <UserPlus className="w-4 h-4" /> Add Owner
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">User</th>
                <th scope="col" className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Email</th>
                <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Ownership</th>
                <th scope="col" className="text-center px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Active</th>
                <th scope="col" className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!vehicle.owners || vehicle.owners.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-gray-400">No owners assigned</td></tr>
              ) : (
                vehicle.owners.map((owner) => (
                  <tr key={owner.userUuid || owner.userId} className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                          {(owner.userName?.[0] || owner.userEmail?.[0] || '?').toUpperCase()}
                        </div>
                        {owner.userId ? (
                          <Link to={`/users/${owner.userId}`} className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors">
                            {owner.userName || 'Unknown'}
                          </Link>
                        ) : (
                          <span className="text-sm font-medium">{owner.userName || 'Unknown'}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">{owner.userEmail || '-'}</td>
                    <td className="px-6 py-4 text-center">
                      <button onClick={() => openEditOwner(owner)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-pointer hover:opacity-80 transition-opacity ${ownershipColors[owner.ownershipType] || 'bg-gray-500/20 text-gray-400'}`}>
                        {owner.ownershipType}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      {owner.active
                        ? <span className="text-green-400 text-sm font-medium">Active</span>
                        : <span className="text-gray-400 text-sm font-medium">Inactive</span>}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => setRemoveOwnerTarget(owner)}
                        className="p-2 hover:bg-red-500/10 rounded-lg transition-colors" aria-label={`Remove ${owner.userName || 'owner'}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Vehicle Modal */}
      <Modal open={editOpen} title="Edit Vehicle" onClose={handleCloseEdit} maxWidth="max-w-lg" footer={
        <>
          <button onClick={handleCloseEdit} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={saveEdit} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{saving ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {saveError && <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">{saveError}</div>}
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Plate" htmlFor="edit-vd-plate" error={formErrors.plate}>
            <input id="edit-vd-plate" type="text" value={editForm.plate || ''} onChange={(e) => updateField('plate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="Status" htmlFor="edit-vd-status">
            <select id="edit-vd-status" value={editForm.status || ''} onChange={(e) => updateField('status', e.target.value)} className={inputCls}>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormField>
        </div>
        <FormField label="Mileage (km)" htmlFor="edit-vd-mileage" error={formErrors.mileage}>
          <input id="edit-vd-mileage" type="number" min="0" value={editForm.mileage ?? ''} onChange={(e) => updateField('mileage', e.target.value)} className={inputCls} />
        </FormField>
        <FormField label="Notes" htmlFor="edit-vd-notes">
          <textarea id="edit-vd-notes" value={editForm.notes || ''} onChange={(e) => updateField('notes', e.target.value)} rows={3} className={`${inputCls} resize-none`} />
        </FormField>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField label="Insurance Expiry" htmlFor="edit-vd-insurance">
            <input id="edit-vd-insurance" type="date" value={editForm.insuranceExpiryDate || ''} onChange={(e) => updateField('insuranceExpiryDate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="MOT Expiry" htmlFor="edit-vd-mot">
            <input id="edit-vd-mot" type="date" value={editForm.technicalInspectionExpiryDate || ''} onChange={(e) => updateField('technicalInspectionExpiryDate', e.target.value)} className={inputCls} />
          </FormField>
          <FormField label="Tax Due" htmlFor="edit-vd-tax">
            <input id="edit-vd-tax" type="date" value={editForm.taxDueDate || ''} onChange={(e) => updateField('taxDueDate', e.target.value)} className={inputCls} />
          </FormField>
        </div>
      </Modal>

      {/* Add Owner Modal */}
      <Modal open={addOwnerOpen} title="Add Owner" onClose={() => setAddOwnerOpen(false)} maxWidth="max-w-md" footer={
        <>
          <button onClick={() => setAddOwnerOpen(false)} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={handleAddOwner} disabled={!selectedUser || addingOwner}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <UserPlus className="w-4 h-4" />{addingOwner ? 'Adding...' : 'Add Owner'}
          </button>
        </>
      }>
        <FormField label="Search User" htmlFor="owner-search">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input id="owner-search" type="text" placeholder="Search by name or email..."
              value={ownerSearch} onChange={(e) => { setOwnerSearch(e.target.value); setSelectedUser(null) }}
              className={`${inputCls} pl-10`} />
          </div>
        </FormField>

        {ownerSearchLoading && <p className="text-sm text-gray-400">Searching...</p>}

        {ownerSearchResults.length > 0 && !selectedUser && (
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-xl">
            {ownerSearchResults.map((u) => (
              <button key={u.id} onClick={() => { setSelectedUser(u); setOwnerSearch(u.name || u.email) }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-left border-b border-gray-100 dark:border-gray-700/50 last:border-0">
                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {(u.name?.[0] || u.email?.[0] || '?').toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-medium">{u.name || 'No name'}</p>
                  <p className="text-xs text-gray-400">{u.email}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        {selectedUser && (
          <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
              {(selectedUser.name?.[0] || selectedUser.email?.[0] || '?').toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">{selectedUser.name || 'No name'}</p>
              <p className="text-xs text-gray-400">{selectedUser.email}</p>
            </div>
            <button onClick={() => { setSelectedUser(null); setOwnerSearch('') }} className="text-xs text-gray-400 hover:text-white">Change</button>
          </div>
        )}

        <FormField label="Ownership Type" htmlFor="owner-type">
          <select id="owner-type" value={ownerType} onChange={(e) => setOwnerType(e.target.value)} className={inputCls}>
            {OWNERSHIP_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
          </select>
        </FormField>
      </Modal>

      {/* Edit Ownership Modal */}
      <Modal open={!!editOwnerTarget} title="Edit Ownership" onClose={() => setEditOwnerTarget(null)} maxWidth="max-w-sm" footer={
        <>
          <button onClick={() => setEditOwnerTarget(null)} className="px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition font-medium text-sm">Cancel</button>
          <button onClick={handleUpdateOwnership} disabled={savingOwner}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 text-sm">
            <Check className="w-4 h-4" />{savingOwner ? 'Saving...' : 'Save'}
          </button>
        </>
      }>
        {editOwnerTarget && (
          <>
            <p className="text-sm text-gray-400">Editing ownership for <strong>{editOwnerTarget.userName || editOwnerTarget.userEmail || 'Unknown'}</strong></p>
            <FormField label="Ownership Type" htmlFor="edit-owner-type">
              <select id="edit-owner-type" value={editOwnerType} onChange={(e) => setEditOwnerType(e.target.value)} className={inputCls}>
                {OWNERSHIP_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}
              </select>
            </FormField>
          </>
        )}
      </Modal>

      {/* Confirm close edit */}
      <ConfirmDialog
        open={confirmClose}
        title="Unsaved changes"
        message="You have unsaved changes. Are you sure you want to close?"
        confirmLabel="Discard"
        onConfirm={() => { setConfirmClose(false); setEditOpen(false) }}
        onCancel={() => setConfirmClose(false)}
      />

      {/* Confirm remove owner */}
      <ConfirmDialog
        open={!!removeOwnerTarget}
        title="Remove Owner"
        message={`Are you sure you want to remove ${removeOwnerTarget?.userName || removeOwnerTarget?.userEmail || 'this user'} from this vehicle?`}
        confirmLabel={removingOwner ? 'Removing...' : 'Remove'}
        variant="danger"
        onConfirm={handleRemoveOwner}
        onCancel={() => setRemoveOwnerTarget(null)}
      />
    </div>
  )
}

export default VehicleDetailPage
